/**
 * PSTUDIO-4453: the one owner of how a node number is represented.
 *
 * A node number is a canonical decimal-integer STRING — the collab document's
 * form (`ProStudioNode.nodeNumber`, backend `canonicalizeNodeNumber`, a DB
 * CHECK of `^(0|-?[1-9][0-9]*)$`): an optional minus, then digits with no
 * leading zeros, never "-0". The table, numbering and validation all carry
 * that string; a JS number appears only where an external contract asks for
 * one (the PIF compiler, `toCompilerNodeNumber`).
 *
 * Which strings are VALID node numbers is a separate question, unchanged by
 * the move to strings: the editor numbers from positive safe integers
 * (`isNumberingNodeNumber`), and validation keeps its own rules
 * (`node-number-identity.ts`, `normalizeNodeNumber`).
 */

const CANONICAL_NODE_NUMBER = /^(0|-?[1-9][0-9]*)$/;
const POSITIVE_NODE_NUMBER = /^[1-9][0-9]*$/;
const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);

/**
 * The canonical form of an integer node number, or undefined when the value is
 * not one. Mirrors the backend case for case, so the table holds what the
 * document will hold: whitespace anywhere, leading zeros, "-0" and a zero
 * fraction ("70.0", as a CSV cell or a Decimal can produce) are normalised;
 * a real fraction, a plus sign, an exponent, hex or letters are not numbers.
 * A JS number counts only as a safe integer; anything else (a boolean, an
 * array — which `Number()` would happily turn into "node 1") is nothing.
 */
export const canonicalNodeNumber = (value: unknown): string | undefined => {
  if (typeof value === 'number') {
    // String(-0) is already '0'.
    return Number.isSafeInteger(value) ? String(value) : undefined;
  }
  if (typeof value !== 'string') return undefined;

  const match = value.replace(/\s+/g, '').match(/^(-?)(\d+)(?:\.0*)?$/);
  if (!match) return undefined;

  const digits = match[2].replace(/^0+(?=\d)/, '');
  return digits === '0' ? digits : match[1] + digits;
};

export const isCanonicalNodeNumber = (value: unknown): value is string =>
  typeof value === 'string' && CANONICAL_NODE_NUMBER.test(value);

/**
 * What a Node # cell holds for any input: the canonical form when there is
 * one, otherwise the text as typed (trimmed) — kept, so validation can flag
 * it, and never quietly read as some other number ("1e3" is not 1000).
 * Blank, null and NaN are an empty cell.
 */
export const toNodeNumberCell = (value: unknown): string | null => {
  const canonical = canonicalNodeNumber(value);
  if (canonical !== undefined) return canonical;
  // A stored number that is not a safe integer (Hasura's `numeric` arrives as
  // a JSON number: a 1.5 saved before) stays as its text, to be flagged —
  // dropping it to an empty cell would let the next save erase it.
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value !== 'string') return null;
  const typed = value.trim();
  return typed === '' ? null : typed;
};

/** Orders two canonical node numbers by value ("31" before "301"). */
export const compareNodeNumbers = (a: string, b: string): number => {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
};

/** `n + by`, exactly, on a canonical node number. */
export const addToNodeNumber = (n: string, by: number): string =>
  (BigInt(n) + BigInt(by)).toString();

/**
 * A number the editor numbers from and allocates: a positive integer no
 * larger than `Number.MAX_SAFE_INTEGER`, in canonical form. The bound is the
 * compiler's, not the string's — the PIF row converts the number with
 * `Number()` (backend `pif.ts`), so anything past it would publish as a
 * different number.
 */
export const isNumberingNodeNumber = (value: unknown): value is string =>
  typeof value === 'string' &&
  POSITIVE_NODE_NUMBER.test(value) &&
  BigInt(value) <= MAX_SAFE;

/**
 * The number a row can be navigated to or looked up by (Go to node, the
 * row's error highlight): the canonical string it holds, or nothing for an
 * empty cell or a value that is not a node number.
 */
export const nodeNumberToGoTo = (value: unknown): string | undefined =>
  isCanonicalNodeNumber(value) ? value : undefined;

// What an <input type="number"> keeps rather than sanitising to "" (the HTML
// "valid floating-point number").
const HTML_NUMBER = /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?$/;

/**
 * The Node # editor's input type for a value. A number input blanks anything
 * the browser does not read as a number, so a value kept as typed ("abc",
 * "0x1F") would look empty while its validation error names it — those get a
 * text input.
 */
export const nodeNumberInputType = (value: string): 'number' | 'text' =>
  value === '' || HTML_NUMBER.test(value) ? 'number' : 'text';
